import React, { useState, useEffect } from 'react';
import { TextField, Button, Typography, Box, CircularProgress, Stack, Alert, Grid, FormControl, Select, InputLabel, MenuItem } from '@mui/material';
import axios from 'utils/axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next'; // <-- i18n hook

const User = () => {
  const { t } = useTranslation(); // <-- translation hook
  const [userData, setUserData] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { identifier } = useParams();
  const navigate = useNavigate();

  const [editing, setEditing] = useState(true);
  const [editedData, setEditedData] = useState({});
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    const fetchUserData = async (id) => {
      try {
        const response = await axios.get('users/' + id);
        const user = response.data;

        setUserData(user);
        setEditedData({
          ...user,
          departmentId: user.departmentId || null,
        });
        setLoading(false);

        const hasSluzba = user.roles?.some(roleObj =>
          Array.isArray(roleObj.role?.additionalData) &&
          roleObj.role.additionalData.includes("sluzba")
        );

        if (hasSluzba) {
          fetchDepartments();
        }

      } catch (err) {
        toast.error(t('failed_to_fetch_user'));
        setLoading(false);
      }
    };

    const fetchDepartments = async () => {
      try {
        const response = await axios.get('codebook?codebook=departments');
        setDepartments(response.data);
      } catch (err) {
        toast.error(t('failed_to_load_departments'));
      }
    };

    fetchUserData(identifier);
  }, [identifier, t]);

  const handleEdit = () => {
    setEditing(true);
    setEditedData(userData);
  };

  const handleSave = async () => {
    try {
      console.log('Saving edited data:', editedData);
      const response = await axios.put('users/' + identifier, editedData);
      setUserData(response.data);
      goBackToUsers();
    } catch (err) {
      toast.error(t('failed_to_save_changes'));
    }
  };

  const goBackToUsers = () => {
    navigate('/users');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box mt={3}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box textAlign="center" mt={3}>
        <Typography variant="h4" gutterBottom>
          {t('user_profile')}
        </Typography>
      </Box>
      {editing ? (
        <Box maxWidth="md" mx="auto" mt={4} component="form" noValidate autoComplete="off">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                label={t('username')}
                value={editedData.username}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                label={t('password')}
                type="password"
                value={editedData.password || ''}
                onChange={(e) => setEditedData({ ...editedData, password: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                label={t('name')}
                value={editedData.name || ''}
                onChange={(e) => setEditedData({ ...editedData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                label={t('surname')}
                value={editedData.surname || ''}
                onChange={(e) => setEditedData({ ...editedData, surname: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                label={t('email')}
                value={editedData.email || ''}
                onChange={(e) => setEditedData({ ...editedData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {editedData.roles && editedData.roles.length > 0 ? (
                editedData.roles.map((roleObj, index) => (
                  <Box key={index} mb={2}>
                    {/* Role Name (read-only) */}
                    <TextField
                      fullWidth
                      margin="normal"
                      label={t('role')}
                      value={roleObj.role.role}
                      disabled
                    />

                    {/* Editable Department */}
                    {departments.length > 0 && (
                      <FormControl fullWidth margin="normal">
                        <InputLabel>{t('department')}</InputLabel>
                        <Select
                          value={roleObj.departmentId || ''}
                          label={t('department')}
                          onChange={(e) => {
                            const value = e.target.value;

                            // ✅ Update both top-level and the specific roleObj
                            setEditedData((prev) => {
                              const updatedRoles = [...prev.roles];
                              updatedRoles[index] = {
                                ...updatedRoles[index],
                                departmentId: value,
                              };

                              return {
                                ...prev,
                                departmentId: value,
                                roles: updatedRoles,
                              };
                            });
                          }}
                        >
                          <MenuItem value="">{t('select_department')}</MenuItem>
                          {departments.map((dept) => (
                            <MenuItem key={dept.id} value={dept.id}>
                              {dept.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                ))
              ) : (
                <Typography>{t('no_roles_assigned')}</Typography>
              )}
            </Grid>

          </Grid>


          <Grid item xs={12} mt={3}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={goBackToUsers}>
                {t('back')}
              </Button>
              <Button type="submit" variant="contained" onClick={handleSave}>
                {t('save_changes')}
              </Button>
            </Box>
          </Grid>

        </Box>
      )

        : (
          <>
            <Typography>ID: {userData.id}</Typography>
            <Typography>{t('username')}: {userData.username}</Typography>
            <Typography>{t('email')}: {userData.email}</Typography>
            <Button variant="contained" onClick={handleEdit} sx={{ mt: 2 }}>
              {t('edit')}
            </Button>
          </>
        )}
    </Box>
  );
};

export default User;
